import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Mail, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECT_TYPES = [
  "App Development",
  "Advanced Manufacturing",
  "Clean Energy",
  "Smart Agriculture",
  "Real Estate",
  "Partnership Proposal",
  "Other",
];

const ContactModal = ({ open, onClose, defaultType }) => {
  const [form, setForm] = useState({ name: "", email: "", project_type: PROJECT_TYPES[0], message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) {
      setForm((f) => ({ ...f, project_type: defaultType || PROJECT_TYPES[0] }));
    }
  }, [open, defaultType]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Transmission received. Our team will respond shortly.");
      setForm({ name: "", email: "", project_type: PROJECT_TYPES[0], message: "" });
      onClose();
    } catch {
      toast.error("Transmission failed. Try the direct email line below.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full border border-white/15 bg-white/[0.03] px-4 py-3.5 text-sm text-white placeholder:text-white/25 outline-none transition-colors duration-300 focus:border-cyber";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[90] flex items-center justify-center bg-[#0B0C10]/85 p-4 backdrop-blur-md"
          onClick={onClose}
          data-testid="contact-modal-backdrop"
        >
          <motion.div
            initial={{ opacity: 0, y: 48, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 32, scale: 0.98 }}
            transition={{ duration: 0.45, ease: [0.33, 1, 0.68, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="hud-corners relative max-h-[90vh] w-full max-w-xl overflow-y-auto border border-white/15 bg-surface p-8 md:p-12"
            data-testid="contact-modal"
            role="dialog"
            aria-modal="true"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-neon">
                  // Initialize Project
                </p>
                <h3 className="mt-4 font-display text-2xl font-medium tracking-tight md:text-3xl">
                  Open a channel.
                </h3>
              </div>
              <button
                data-testid="contact-modal-close"
                onClick={onClose}
                aria-label="Close contact form"
                className="flex h-10 w-10 items-center justify-center border border-white/15 text-white/60 transition-colors duration-300 hover:border-cyber hover:text-cyber"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <form onSubmit={submit} className="mt-10 space-y-6" data-testid="contact-form">
              <div>
                <label htmlFor="contact-name" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.3em] text-white/45">
                  Name
                </label>
                <input
                  id="contact-name"
                  data-testid="contact-name-input"
                  required
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Your designation"
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.3em] text-white/45">
                  Email
                </label>
                <input
                  id="contact-email"
                  data-testid="contact-email-input"
                  required
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  placeholder="you@company.com"
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="contact-type" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.3em] text-white/45">
                  Project Type
                </label>
                <select
                  id="contact-type"
                  data-testid="contact-type-select"
                  value={form.project_type}
                  onChange={set("project_type")}
                  className={`${inputCls} appearance-none bg-surface`}
                >
                  {PROJECT_TYPES.map((t) => (
                    <option key={t} value={t} className="bg-surface">
                      {t}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="contact-message" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.3em] text-white/45">
                  Message
                </label>
                <textarea
                  id="contact-message"
                  data-testid="contact-message-input"
                  required
                  rows={4}
                  value={form.message}
                  onChange={set("message")}
                  placeholder="Describe the mission parameters..."
                  className={`${inputCls} resize-none`}
                />
              </div>

              <button
                type="submit"
                data-testid="contact-submit-button"
                disabled={sending}
                className="w-full border border-neon bg-neon/10 py-4 font-mono text-[11px] uppercase tracking-[0.3em] text-neon transition-all duration-300 hover:bg-neon hover:text-[#0B0C10] hover:drop-shadow-[0_0_16px_rgba(0,255,148,0.5)] disabled:opacity-50"
              >
                {sending ? "Transmitting..." : "Transmit Enquiry"}
              </button>

              <a
                data-testid="contact-mailto-link"
                href="mailto:bureau@futurefactory.group"
                className="flex items-center justify-center gap-2 pt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 transition-colors duration-300 hover:text-cyber"
              >
                <Mail className="h-3 w-3" />
                Prefer direct line: bureau@futurefactory.group
              </a>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ContactModal;
